import { CaretDown, CaretUp, ChatCircleText, CheckCircle, Clock, Sparkle, WarningCircle } from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { colors, spacing } from "../../../../styles/design-system";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import { type ObjectiveAnalytics } from "./insightsTypes";

type ModuleInsightsObjectiveItemProps = {
  objective: ObjectiveAnalytics;
  objectiveIndex: number;
  defaultExpanded?: boolean;
};

const ModuleInsightsObjectiveItem: React.FC<ModuleInsightsObjectiveItemProps> = ({
  objective,
  objectiveIndex,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const progress = objective.progress_details;
  const statusLabel =
    objective.confusion_points >= 1 ? "Progressing" : objective.success_points > 0 ? "Mastered" : "Not Started";
  const statusColor =
    objective.confusion_points >= 1
      ? colors.warning[700]
      : objective.success_points > 0
        ? colors.success[700]
        : colors.grey[500];
  const summaryLines = useMemo(() => (progress?.summary ? [progress.summary] : []), [progress?.summary]);
  const successLines = useMemo(
    () => progress?.success_points?.filter(Boolean).map(String) ?? [],
    [progress?.success_points]
  );
  const confusionLines = useMemo(
    () => progress?.confusion_points?.filter(Boolean).map(String) ?? [],
    [progress?.confusion_points]
  );
  const recommendationLines = useMemo(
    () => progress?.further_recommendations?.filter(Boolean).map(String) ?? [],
    [progress?.further_recommendations]
  );
  const averageTimeText =
    typeof objective.average_time_taken === "number" ? formatDurationFromSeconds(objective.average_time_taken) : "—";
  const hasExpandedSections =
    summaryLines.length > 0 || successLines.length > 0 || confusionLines.length > 0 || recommendationLines.length > 0;

  return (
    <View style={styles.container}>
      <Pressable style={styles.header} onPress={() => setExpanded((prev) => !prev)}>
        <View style={styles.headerText}>
          <CustomText
            text={`${objectiveIndex}. ${objective.label}`}
            size="s"
            weight="medium"
            useTranslationText={false}
          />
          <View style={styles.metaRow}>
            <CustomText
              text={statusLabel}
              size="xs"
              weight="medium"
              useTranslationText={false}
              style={{ color: statusColor }}
            />
            <View style={styles.metaItem}>
              <ChatCircleText size={12} color={colors.grey[500]} />
              <CustomText
                text={`${progress?.questions_asked ?? 0} qs`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <Clock size={12} color={colors.grey[500]} />
              <CustomText text={averageTimeText} size="xs" useTranslationText={false} style={styles.mutedText} />
            </View>
          </View>
        </View>
        {expanded ? <CaretUp size={14} color={colors.grey[500]} /> : <CaretDown size={14} color={colors.grey[500]} />}
      </Pressable>

      {expanded ? (
        <View style={styles.content}>
          {hasExpandedSections ? (
            <>
              {summaryLines.length > 0 ? (
                <View style={[styles.detailSection, styles.summarySection]}>
                  <View style={styles.detailTitleRow}>
                    <Sparkle size={14} color={colors.brand[700]} />
                    <CustomText text={summaryLines[0]} size="s" useTranslationText={false} style={styles.detailText} />
                  </View>
                </View>
              ) : null}

              {successLines.length > 0 ? (
                <View style={[styles.detailSection, styles.successSection]}>
                  <View style={styles.detailTitleRow}>
                    <CheckCircle size={14} color={colors.success[700]} weight="fill" />
                    <CustomText text="What Went Well" size="s" weight="bold" useTranslationText={false} />
                  </View>
                  {successLines.map((line, index) => (
                    <CustomText
                      key={`success-${objective.id}-${index}`}
                      text={`• ${line}`}
                      size="s"
                      useTranslationText={false}
                      style={styles.detailText}
                    />
                  ))}
                </View>
              ) : null}

              {confusionLines.length > 0 ? (
                <View style={[styles.detailSection, styles.warningSection]}>
                  <View style={styles.detailTitleRow}>
                    <WarningCircle size={14} color={colors.error[700]} weight="fill" />
                    <CustomText text="Areas of Confusion" size="s" weight="bold" useTranslationText={false} />
                  </View>
                  {confusionLines.map((line, index) => (
                    <CustomText
                      key={`confusion-${objective.id}-${index}`}
                      text={`• ${line}`}
                      size="s"
                      useTranslationText={false}
                      style={styles.detailText}
                    />
                  ))}
                </View>
              ) : null}

              {recommendationLines.length > 0 ? (
                <View style={[styles.detailSection, styles.summarySection]}>
                  <View style={styles.detailTitleRow}>
                    <Sparkle size={14} color={colors.brand[700]} />
                    <CustomText text="AI Recommendations" size="s" weight="bold" useTranslationText={false} />
                  </View>
                  {recommendationLines.map((line, index) => (
                    <CustomText
                      key={`recommendation-${objective.id}-${index}`}
                      text={`• ${line}`}
                      size="s"
                      useTranslationText={false}
                      style={styles.detailText}
                    />
                  ))}
                </View>
              ) : null}
            </>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 10,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  mutedText: {
    color: colors.grey[500],
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    gap: spacing.xs,
  },
  detailSection: {
    borderLeftWidth: 2,
    paddingLeft: spacing.sm,
    paddingRight: spacing.xs,
    paddingVertical: spacing.xs,
    gap: 6,
  },
  summarySection: {
    borderLeftColor: colors.brand[200],
  },
  successSection: {
    borderLeftColor: colors.success[200],
  },
  warningSection: {
    borderLeftColor: colors.error[200],
  },
  detailTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  detailText: {
    color: colors.grey[700],
  },
});

export default ModuleInsightsObjectiveItem;
